import React, { useState } from 'react';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PageWrapper from 'components/Common/PageWraper';
import PageHeader from 'components/Header/Page';
import DeleteConfirmationModal from 'components/Modal/DeleteConfirmationModal';
import DeleteIcon from '@mui/icons-material/Delete';
import PepwiseToast from 'components/Widgets/PepwiseToast';
import { type SearchableDropdownOption } from 'components/Dropdown/SearchableDropdown';
import ReusableConfigPanel, {
  type ReusableConfigField,
  type ReusableConfigPanelValues,
} from 'components/SideDrawer/ReusableConfigPanel';
import ConfigurableSlaFormModal, { SlaFormValues } from 'components/Modal/ConfigurableSlaFormModal';

const projectOptions: SearchableDropdownOption[] = [
  { label: 'Projectname', value: 'project-1' },
  { label: 'Demo Project 1', value: 'project-2' },
  { label: 'Demo Project 2', value: 'project-3' },
];

const slaTypeOptions: SearchableDropdownOption[] = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
];

const dayOptions: SearchableDropdownOption[] = [
  { label: 'All Days', value: 'all-days' },
  { label: 'Weekdays', value: 'weekdays' },
  { label: 'Weekends', value: 'weekends' },
];

const slaDateOptions: SearchableDropdownOption[] = [
  { label: '0', value: '0' },
  { label: '1', value: '1' },
  { label: '2', value: '2' },
];

const slaStartTimeOptions: SearchableDropdownOption[] = [
  { label: '09:10', value: '09:10' },
  { label: '10:00', value: '10:00' },
  { label: '11:00', value: '11:00' },
];

const slaCriteriaOptions: SearchableDropdownOption[] = [
  {
    label: 'All Jobs Completed in Last 24 Hrs',
    value: 'all-jobs-last-24-hrs',
  },
  {
    label: 'All Jobs Completed in Last 12 Hrs',
    value: 'all-jobs-last-12-hrs',
  },
  {
    label: 'Critical Jobs Completed',
    value: 'critical-jobs-completed',
  },
];

const slaFields: ReusableConfigField[] = [
  {
    key: 'project',
    type: 'dropdown',
    label: 'Select Project',
    placeholder: 'Select Project',
    required: true,
    options: projectOptions,
  },
  {
    key: 'slaType',
    type: 'dropdown',
    label: 'SLA Type',
    placeholder: 'Select SLA Type',
    required: true,
    options: slaTypeOptions,
  },
  {
    key: 'days',
    type: 'dropdown',
    label: 'Days',
    placeholder: 'Select Days',
    required: true,
    options: dayOptions,
  },
  {
    key: 'slaDate',
    type: 'dropdown',
    label: 'SLA Date',
    placeholder: 'Select SLA Date',
    required: true,
    options: slaDateOptions,
  },
  {
    key: 'slaStartTime',
    type: 'dropdown',
    label: 'SLA Start Time',
    placeholder: 'Select SLA Start Time',
    required: true,
    options: slaStartTimeOptions,
  },
  {
    key: 'slaCriteria',
    type: 'dropdown',
    label: 'SLA Criteria',
    placeholder: 'Select SLA Criteria',
    required: false,
    options: slaCriteriaOptions,
  },
];

const jobOptions: SearchableDropdownOption[] = [
  { label: 'jobname1', value: 'jobname1' },
  { label: 'jobname2', value: 'jobname2' },
  { label: 'jobname3', value: 'jobname3' },
];

const conditionOptions: SearchableDropdownOption[] = [
  { label: 'More', value: 'more' },
  { label: 'Less', value: 'less' },
  { label: 'Equal', value: 'equal' },
];

const countOptions: SearchableDropdownOption[] = [
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5', value: 5 },
];

const criteriaOptions: SearchableDropdownOption[] = [
  {
    label: 'All Jobs Completed By Same Day',
    value: 'same_day',
  },
  {
    label: 'All Jobs Completed By Next Day',
    value: 'next_day',
  },
  {
    label: 'At Least One Job Missed',
    value: 'one_missed',
  },
];

export default function ModalsPage() {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [visibleToasts, setVisibleToasts] = React.useState({
    success: true,
    alert: true,
    error: true,
  });

  const [openSlaForm, setOpenSlaForm] = React.useState(false);

  const handleSave = (values: SlaFormValues) => {
    console.log('Saved SLA config:', values);
    setOpenSlaForm(false);
  };

  const handleClose = (type: 'success' | 'alert' | 'error') => {
    setVisibleToasts((prev) => ({
      ...prev,
      [type]: false,
    }));
  };

  const handleDeleteConfirm = async () => {
    console.log('Deleting....');
    setOpenDialog(false);
  };

  const getInitialValues = (): ReusableConfigPanelValues => ({
    project: null,
    slaType: null,
    days: null,
    slaDate: null,
    slaStartTime: null,
    slaCriteria: null,
  });

  const [values, setValues] = React.useState(getInitialValues());

  const handleConfigClose = () => {
    console.log('Closing Config Panel');
    setOpen(false);
    setValues(getInitialValues());
  };

  const handleFieldChange = (fieldKey: string, value: SearchableDropdownOption | null) => {
    setValues((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const requiredFields = slaFields.filter((field) => field.required);
  const isSaveDisabled = requiredFields.some((field) => !values[field.key]);

  const handleConfigSave = () => {
    console.log('Saved values:', values);
    setOpen(false);
  };

  return (
    <PageWrapper>
      <PageHeader title="Modals & Overlays Demo">
        <Button variant="pepwiseSecondary" onClick={() => navigate(-1)}>
          ← Back
        </Button>
      </PageHeader>

      <Grid container spacing={3} mt={2}>
        <Grid item xs={12} md={3}>
          <Box sx={{ backgroundColor: '#FFFFFF', p: 2 }}>
            <Typography sx={{ color: '#000000', marginBottom: 2, fontWeight: 500 }}>
              Click on Below Delete button to view the Delete Confirmation modal
            </Typography>

            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setOpenDialog(true)}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
              }}
            >
              Delete
            </Button>
          </Box>

          <DeleteConfirmationModal
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            onConfirm={handleDeleteConfirm}
            identifierName="detl_dy_wf_m_case3"
            type="Job Schedule"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <Box sx={{ backgroundColor: '#FFFFFF', p: 2 }}>
            <Typography sx={{ color: '#000000', marginBottom: 2, fontWeight: 500 }}>
              Click on Below button to view SLA form popup.
            </Typography>

            <Button
              variant="contained"
              onClick={() => setOpenSlaForm(true)}
              sx={{
                backgroundColor: '#39ACE7',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#2f98d0',
                  boxShadow: 'none',
                },
              }}
            >
              Open SLA Form Popup
            </Button>

            <ConfigurableSlaFormModal
              open={openSlaForm}
              onClose={() => setOpenSlaForm(false)}
              onSave={handleSave}
              title="Configure SLA For Demo Project 1"
              jobOptions={jobOptions}
              slaTypeOptions={slaTypeOptions}
              conditionOptions={conditionOptions}
              countOptions={countOptions}
              criteriaOptions={criteriaOptions}
              labels={{
                modalTitle: 'Configure SLA',
                rowSectionTitle: 'Configure SLA Start - End Time',
                criticalityTitle: 'SLA Criticality',
                addMoreLabel: 'Add More',
                saveButtonLabel: 'Save',
                cancelButtonLabel: 'Cancel',
              }}
              minRows={1}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={3}>
          <Box
            sx={{
              backgroundColor: '#FFFFFF',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography sx={{ color: '#000000', marginBottom: 2, fontWeight: 500 }}>
              Click on Below button to view the SLA Config Panel
            </Typography>
            <Button
              variant="contained"
              onClick={() => setOpen(true)}
              sx={{
                backgroundColor: '#39ACE7',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#1F8FCA',
                  boxShadow: 'none',
                },
              }}
            >
              Open SLA Config Panel
            </Button>
          </Box>

          <ReusableConfigPanel
            open={open}
            title="Configure SLA For Demo Project 1"
            fields={slaFields}
            values={values}
            onFieldChange={handleFieldChange}
            onClose={handleConfigClose}
            onSave={handleConfigSave}
            saveDisabled={isSaveDisabled}
          />
        </Grid>

        <Grid xs={12} md={6}>
          <Box
            sx={{
              minHeight: '100vh',
              p: 5,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Stack spacing={4} width="100%" alignItems="center">
              <PepwiseToast
                open={visibleToasts.success}
                type="success"
                title="Successful Message"
                message="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Turpis vitae ultrices in sed."
                // autoHideDuration={6000}
                onClose={() => handleClose('success')}
              />

              <PepwiseToast
                open={visibleToasts.alert}
                type="alert"
                title="Alert Message"
                message="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Turpis vitae ultrices in sed."
                autoHideDuration={5000}
                onClose={() => handleClose('alert')}
              />

              <PepwiseToast
                open={visibleToasts.error}
                type="error"
                title="Error Message"
                message="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Turpis vitae ultrices in sed."
                autoHideDuration={5000}
                onClose={() => handleClose('error')}
              />
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </PageWrapper>
  );
}
